import Head from 'next/head'
import { useState, useEffect } from 'react'
import { node, string, bool, shape, object, arrayOf, oneOf, number } from 'prop-types'

import { getPageViewEventData } from '@/utils/datalayer'
import { getScriptsToLoad, SCRIPTS } from '@/utils/headScripts'

/* TODO: move to shared logic across apps (PD-15119) */
import useDeferScripts from '@/hooks/useDeferScripts'

import { navigationDataType } from '@/types'
import Banner from '@/layouts/Banner'
import Nav from '@/layouts/Nav'
import Footer from '@/layouts/Footer'
import { AccessibleQuickMenu } from '@saatva-bits/pattern-library.modules.navigation'
/* TODO: move to shared logic across apps (PD-15120) */
import ThirdPartyScripts from '@/layouts/ThirdPartyScripts'

import { LaunchDarklyWidget, LaunchDarklyWrapper } from '@saatva-bits/pattern-library.modules.launch-darkly/dist/launch-darkly.components'
import { useChatAutoPopup } from '@saatva-bits/pattern-library.utils.chat'

const API_PATH = `${process.env.NEXT_PUBLIC_BASE_PATH}/api`

export default function DefaultLayout({
    children,
    pageType = '',
    excludedScripts = [],
    description,
    title,
    displayLDWidget,
    displaySiteConfig,
    launchDarklyContextData,
    navigationData,
    showBanner = true,
    viewingRoomData,
    cartTotalItems,
    footerData,
    mktChannel
}) {
    useDeferScripts()
    useChatAutoPopup(90000)
    const [showLDWidget, setShowLDWidget] = useState(false)

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setShowLDWidget(displayLDWidget || displaySiteConfig)
        }
    }, [displayLDWidget, displaySiteConfig])

    const { experiments, flagOverrides } = launchDarklyContextData
    const pageViewEvent = getPageViewEventData(pageType, experiments, flagOverrides)

    const loadedScripts = getScriptsToLoad(excludedScripts)

    const {NEXT_PUBLIC_APP_ENV, NEXT_PUBLIC_BASE_PATH} = process.env

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta name="format-detection" content="telephone=no"></meta>
                <meta name="description" content={description || 'The highest quality handcrafted mattresses, bedding & furniture at the most comfortable prices.'} />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                {NEXT_PUBLIC_APP_ENV !== 'production' && <meta name="robots" content="noindex,nofollow" />}
                <link rel="icon" href={`${NEXT_PUBLIC_BASE_PATH}/favicon.ico`} />
                <script dangerouslySetInnerHTML={{
                    __html: `
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push(${JSON.stringify(pageViewEvent)});
                    `
                }} />
                <ThirdPartyScripts
                    loadAffirm={loadedScripts.affirm}
                    loadInstana={loadedScripts.instana}
                    loadGTM={loadedScripts.gtm}
                    loadExtend={loadedScripts.extend}
                    loadTrueVault={loadedScripts.trueVault}
                />
            </Head>
            <main>
                <LaunchDarklyWrapper flagKey="ENABLE_ACCESSIBLE_MENU">
                    <AccessibleQuickMenu />
                </LaunchDarklyWrapper>
                { showBanner && <Banner mktChannel={mktChannel} /> }
                <Nav
                    navigationData={navigationData}
                    cartTotalItems={cartTotalItems}
                    viewingRoomData={viewingRoomData}
                />
                {children}
                <Footer footerData={footerData}/>
                { showLDWidget &&
                    <LaunchDarklyWidget flagApiUrl={`${API_PATH}/flag-data`} tokenApiUrl={`${API_PATH}/edge-token`} advanced={displaySiteConfig} bypassCacheParam={null} />
                }
            </main>
        </>
    )
}

DefaultLayout.propTypes = {
    children: node.isRequired,
    excludedScripts: arrayOf(oneOf(Object.values(SCRIPTS))),
    description: string,
    title: string.isRequired,
    displayLDWidget: bool,
    displaySiteConfig: bool,
    launchDarklyContextData: shape({
        experiments: object,
        featureFlags: object,
        flagOverrides: object
    }),
    navigationData: navigationDataType,
    showBanner: bool,
    cartTotalItems: number.isRequired,
    viewingRoomData: shape({
        vrKey: string,
        hideVrIcon: bool,
        url: string
    }),
    mktChannel: string,
}
